/**
 * Live events for one account from the Workspace socket (ADR 0022), scoped to
 * that account (ADR 0024 decision 5). One connection per account, shared by
 * every subscriber. Live listeners get new events only; history listeners
 * (the Activity tab, ADR 0027) get the last hour the server replays plus
 * everything since, as one list.
 */
import { api } from '../api/client'
import { CRM_FRAME_PATH } from '../crm/crmBridge'
import type { LiveEvent } from './ChatsDeps'

type Listener = (ev: LiveEvent) => void
type HistoryListener = (events: LiveEvent[]) => void

/** Events kept for the history listeners (the server keeps an hour). */
const HISTORY_MS = 60 * 60 * 1000
const HISTORY_MAX = 500

interface Conn {
  listeners: Set<Listener>
  history: { events: LiveEvent[]; replayed: boolean; listeners: Set<HistoryListener> }
  close: () => void
}

const conns = new Map<string, Conn>()

function socketUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}${CRM_FRAME_PATH}workspace/ws`
}

function keep(events: LiveEvent[]): LiveEvent[] {
  const since = Date.now() - HISTORY_MS
  return events.filter((e) => (Date.parse(e.occurred_at) || 0) >= since).slice(-HISTORY_MAX)
}

function open(accountId: string, listeners: Set<Listener>, history: Conn['history']): () => void {
  let closed = false
  let attempt = 0
  let retry: number | null = null
  let ws: WebSocket | null = null

  const connect = () => {
    ws = new WebSocket(socketUrl())
    const sock = ws
    sock.onopen = () => sock.send(JSON.stringify({ type: 'auth', token: api.getToken() }))
    sock.onmessage = (msg) => {
      let frame: { type?: string; event?: LiveEvent; events?: LiveEvent[] }
      try {
        frame = JSON.parse(String(msg.data))
      } catch {
        return
      }
      if (frame.type === 'connected') {
        attempt = 0
        sock.send(JSON.stringify({ type: 'scope', accountIds: [accountId] }))
      } else if (frame.type === 'replay') {
        const seen = new Set(history.events.map((e) => e.id))
        history.events = keep([...(frame.events ?? []).filter((e) => e.account_id === accountId && !seen.has(e.id)), ...history.events])
        history.replayed = true
        history.listeners.forEach((fn) => fn(history.events))
      } else if (frame.type === 'event' && frame.event) {
        const ev = frame.event
        listeners.forEach((fn) => fn(ev))
        if (ev.account_id === accountId && !history.events.some((e) => e.id === ev.id)) {
          history.events = keep([...history.events, ev])
          history.listeners.forEach((fn) => fn(history.events))
        }
      }
    }
    sock.onclose = (e) => {
      if (closed) return
      // 4001: the server refused the token; refresh it before trying again.
      if (e.code === 4001) void api.refreshAccessToken()
      attempt += 1
      retry = window.setTimeout(connect, Math.min(30_000, 1000 * 2 ** Math.min(attempt - 1, 5)))
    }
  }
  connect()
  return () => {
    closed = true
    if (retry) window.clearTimeout(retry)
    ws?.close()
  }
}

function connFor(accountId: string): Conn {
  let conn = conns.get(accountId)
  if (!conn) {
    const listeners = new Set<Listener>()
    const history: Conn['history'] = { events: [], replayed: false, listeners: new Set() }
    conn = { listeners, history, close: open(accountId, listeners, history) }
    conns.set(accountId, conn)
  }
  return conn
}

function release(accountId: string, conn: Conn) {
  if (conn.listeners.size || conn.history.listeners.size) return
  conn.close()
  if (conns.get(accountId) === conn) conns.delete(accountId)
}

export function subscribeAccount(accountId: string, fn: Listener): () => void {
  const conn = connFor(accountId)
  conn.listeners.add(fn)
  return () => {
    conn.listeners.delete(fn)
    release(accountId, conn)
  }
}

/** The account's events of the last hour, now and whenever they change (after the server's replay). */
export function subscribeAccountHistory(accountId: string, fn: HistoryListener): () => void {
  const conn = connFor(accountId)
  conn.history.listeners.add(fn)
  if (conn.history.replayed) fn(keep(conn.history.events))
  return () => {
    conn.history.listeners.delete(fn)
    release(accountId, conn)
  }
}
